import { inject, Injectable } from '@angular/core';
import {
  ApiDependencyProviderService,
  CollectionRequest,
  CollectionResponse,
  CreateResourceResponse,
  HttpParams,
} from '@web/common';
import { map, Observable } from 'rxjs';
import { blank, Guid } from '@web/utils';
import { AdminBrandBannerCollectionItemResponse } from '../responses/admin-brand-banner-collection-item.response';
import { AdminBrandBannerCreateRequest } from '../requests/admin-brand-banner-create.request';
import { AdminBrandBannerDetailsResponse } from '../responses/admin-brand-banner-details.response';
import { AdminBrandBannerUpdateRequest } from '../requests/admin-brand-banner-update.request';

@Injectable({
  providedIn: 'root',
})
export class AdminBrandBannerApiService {
  private readonly provider = inject(ApiDependencyProviderService);

  public fetchCollection(options: {
    brandId: Guid;
    request: CollectionRequest;
  }): Observable<CollectionResponse<AdminBrandBannerCollectionItemResponse>> {
    const { brandId, request } = options;
    const path = `admin/cosmetic-brand/${HttpParams.Id}/banner`;
    const url = this.provider.httpUrlFactory.complex((builder) =>
      builder
        .host(this.hostUrl())
        .path(path)
        .params({ [HttpParams.Id]: brandId })
        .queryParams(
          this.provider.httpHelper.collectionRequestToQueryParams(request),
        )
        .build(),
    );
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToCollectionResponse(
            AdminBrandBannerCollectionItemResponse,
          ),
        ),
      );
  }

  public fetchDetailsById(options: {
    brandId: Guid;
    bannerId: Guid;
  }): Observable<AdminBrandBannerDetailsResponse> {
    const { brandId, bannerId } = options;
    const path = `admin/cosmetic-brand/${HttpParams.Id}/banner/${HttpParams.BannerId}`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.BannerId]: bannerId,
      [HttpParams.Id]: brandId,
    });
    const context = this.provider.httpContextBuilder
      .skipByLoadingInterceptor()
      .skipByHttpErrorInterceptor()
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToResponse(
            AdminBrandBannerDetailsResponse,
          ),
        ),
      );
  }

  public create(options: {
    brandId: Guid;
    request: AdminBrandBannerCreateRequest;
  }): Observable<CreateResourceResponse> {
    const { brandId, request } = options;
    const path = `admin/cosmetic-brand/${HttpParams.Id}/banner`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Id]: brandId,
    });
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .build();
    const body = this.provider.httpHelper.requestToBody(
      request,
      AdminBrandBannerCreateRequest,
    );

    return this.provider.http
      .post(url, body, { context })
      .pipe(
        map(this.provider.httpHelper.bodyToResponse(CreateResourceResponse)),
      );
  }

  public update(options: {
    brandId: Guid;
    bannerId: Guid;
    request: AdminBrandBannerUpdateRequest;
  }): Observable<void> {
    const { brandId, bannerId, request } = options;
    const path = `admin/cosmetic-brand/${HttpParams.Id}/banner/${HttpParams.BannerId}`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.BannerId]: bannerId,
      [HttpParams.Id]: brandId,
    });
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .build();
    const body = this.provider.httpHelper.requestToBody(
      request,
      AdminBrandBannerUpdateRequest,
    );

    return this.provider.http.put(url, body, { context }).pipe(blank());
  }

  public remove(options: { brandId: Guid; bannerId: Guid }): Observable<void> {
    const { brandId, bannerId } = options;
    const path = `admin/cosmetic-brand/${HttpParams.Id}/banner/${HttpParams.BannerId}`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.BannerId]: bannerId,
      [HttpParams.Id]: brandId,
    });
    const context = this.provider.httpContextBuilder.build();

    return this.provider.http.delete(url, { context }).pipe(blank());
  }

  private hostUrl(): string {
    return this.provider.environment.ApiHostUrl.Shop;
  }
}
